import React from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import SearchBar from 'components/atomic/organisms/search-bar';
import SuggestedLocationDropdown from 'components/suggested-location-dropdown';
import SearchIcon from 'components/atomic/atoms/icons/search-icon';
import CloseIcon from 'components/icon/close-icon';
import { buildClassName } from 'utils/build-class-name';
import { mapPageIds } from 'constants/test-constants';
import { SEARCH_OPTION_BUY, SearchOptions } from '@tenants/types/theme';
import { useLocationSearch } from './use-location-search';
import styles from './header-search.module.scss';

import type SearchPrediction from 'data/search-predictions';

//#region Types
export interface HeaderSearchProps {
  /** Current search query */
  locationQuery: string;
  /** Whether the dropdown is currently active */
  isLocationDropdownActive: boolean;
  /** Whether this is an area search */
  isAreaSearch?: boolean;
  /** Handler to update the search query */
  setLocationQuery: (query: string) => void;
  /** Handler to toggle dropdown visibility */
  setIsLocationDropdownActive: (isActive: boolean) => void;
  /** Handler to set the active prediction */
  setActivePrediction?: (searchPrediction: SearchPrediction) => void;
  /** Handler called when a search prediction is clicked */
  onClick: (searchPrediction: SearchPrediction) => void;
  /** Additional CSS class */
  className?: string;
  /** Currently chosen area tag */
  chosenArea?: string;
  /** Handler to update chosen area */
  setChosenArea?: (area: string) => void;
  /** Custom placeholder text */
  placeholder?: string;
  /** Search option type */
  searchOption?: SearchOptions;
  /** Handler for search icon click with predictions */
  onSearchIconClick?: (searchPredictions?: SearchPrediction[], query?: string) => void;
  /** Analytics handler for search bar click */
  onSearchBarClickAnalytics?: () => void;
}
//#endregion

/**
 * LocationSearch - Domain component for location search functionality
 * 
 * Component Layer: Domain (src/ui)
 * 
 * This component composes atomic components (SearchBar, IconInput)
 * with domain-specific logic for property location search.
 * 
 * Features:
 * - Location autocomplete with suggestions dropdown
 * - Clear button when input has value
 * - Search icon for submitting
 * - Area tag display
 * - Multi-tenant theming (Zoocasa, eXp)
 * 
 * @example
 * ```tsx
 * <LocationSearch
 *   locationQuery={query}
 *   setLocationQuery={setQuery}
 *   isLocationDropdownActive={isOpen}
 *   setIsLocationDropdownActive={setIsOpen}
 *   onClick={handlePredictionClick}
 *   placeholder="Search by city, address, or postal code"
 * />
 * ```
 */
export function HeaderSearch({
  locationQuery,
  isLocationDropdownActive,
  isAreaSearch = false,
  setLocationQuery,
  setIsLocationDropdownActive,
  setActivePrediction = (_: SearchPrediction) => undefined,
  onClick,
  className,
  chosenArea,
  setChosenArea,
  placeholder,
  searchOption = SEARCH_OPTION_BUY,
  onSearchIconClick,
  onSearchBarClickAnalytics,
}: HeaderSearchProps) {
  const { t } = useTranslation(Namespaces.SEARCH);

  const {
    // State
    isLoadingSearchPredictions,
    searchPredictions,
    searchAgentPredictions,
    isTyping,
    isAgentSearch,
    showMiddleIcon,
    
    // Handlers
    handleInputChange,
    handleInputClick,
    handleInputFocus,
    handleInputBlur,
    handleSearchIconClick,
    handleClearClick,
    handleSuggestedLocationClick,
    handleKeyDown,
    handleClickOutside,
  } = useLocationSearch({
    locationQuery,
    setLocationQuery,
    isLocationDropdownActive,
    setIsLocationDropdownActive,
    onClick,
    setActivePrediction,
    searchOption,
    isAreaSearch,
    chosenArea,
    setChosenArea,
    onSearchIconClick,
    onSearchBarClickAnalytics,
  });

  // Default placeholder with i18n
  const defaultPlaceholder = t(LOCALE_SEARCH_PATHS.LOCATION, { defaultValue: 'Location' });
  const finalPlaceholder = placeholder || defaultPlaceholder;

  // Render suggestions dropdown
  const renderDropdown = () => (
    <SuggestedLocationDropdown
      isLoading={isLoadingSearchPredictions}
      searchPredictions={searchPredictions}
      setActivePrediction={setActivePrediction}
      onClick={handleSuggestedLocationClick}
      isActive={isLocationDropdownActive}
      isAreaSearch={isAreaSearch}
      isAdvancedSearch={true}
      locationQuery={locationQuery}
      isTyping={isTyping}
      hideSearchPanel={() => setIsLocationDropdownActive(false)}
      disablePopularSearches={isAgentSearch || isAreaSearch}
      searchAgentPredictions={searchAgentPredictions}
      className={styles['suggested-location-dropdown']}
    />
  );

  return (
    <SearchBar
      inputProps={{
        value: locationQuery,
        onValueChange: handleInputChange,
        placeholder: finalPlaceholder,
        onClick: handleInputClick,
        onFocus: handleInputFocus,
        onBlur: handleInputBlur,
        onKeyDown: handleKeyDown,
        onSubmit: handleSearchIconClick,
        leftIcon: <SearchIcon className={styles['search-icon']} />,
        middleIcon: <CloseIcon />,
        showMiddleIcon,
        onMiddleIconClick: handleClearClick,
        tag: chosenArea,
        onTagRemove: handleClearClick,
        testId: mapPageIds.mapPageSearchbar,
        testIdMiddleIcon: 'mapPageSearchbarClear',
        className: buildClassName(
          styles.locationInput
        ),
      }}
      dropdown={renderDropdown()}
      isDropdownOpen={isLocationDropdownActive}
      onClickOutside={handleClickOutside}
      className={buildClassName(styles.component, className)}
    />
  );
}

export default HeaderSearch;
